import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Tooltip,
  Chip,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  TextField,
  ClickAwayListener,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Typography, PageHeader } from '@/components/ui';
import { toast } from 'react-hot-toast';
import { AxiosError } from 'axios';
import {
  Visibility as ViewIcon,
  CloudDownload as DownloadIcon,
  Fingerprint as FingerprintIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  FilterList as FilterIcon,
  ArrowDownward as ArrowDownwardIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import type { PageHeaderAction } from '@/components/ui/PageHeader';
import { useCommonBreadcrumbs } from '@/utils/breadcrumbs';
import { getBreadcrumbConfig } from '@/config/breadcrumbs';
import { formatCurrency } from '@/utils/formatters';
import { format as formatMomentDate } from '@/utils/time';
import { SettlementSummaryCards } from '../../components/SettlementSummaryCards';
import { api } from '@/api/client';
import { settlementListService } from '@/superadmin/services/settlementListService';
import { settlementService } from '@/superadmin/services/settlementService';
import GenerateSettlementDrawer from '../../components/SettlementSummaryCards/GenerateSettlementDrawer';
import { FilterPanel } from '../../components/SettlementFilters';
import type { SettlementFilters } from '@/services/settlementService';
import { useSuperadminAuth } from '@/superadmin/contexts/SuperadminAuthContext';

type MerchantOption = {
  value: string;
  label: string;
};

type UnknownRecord = Record<string, any>;

const extractArrayFromPayload = (payload: any): any[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && typeof payload === 'object') {
    const record = payload as UnknownRecord;
    for (const key of ['data', 'results', 'items', 'records', 'rows', 'list', 'merchants']) {
      if (key in record) {
        const nested = extractArrayFromPayload(record[key]);
        if (nested.length) {
          return nested;
        }
      }
    }
  }

  return [];
};

const normalizeMerchantOptions = (payload: any): MerchantOption[] => {
  const entries = extractArrayFromPayload(payload);
  const seen = new Set<string>();

  return entries
    .map((entry): MerchantOption | null => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const data = entry as UnknownRecord;
      const rawValue =
        data.merchant_uuid ?? data.uuid ?? data.id ?? data.merchantId ?? data.merchantID;

      if (!rawValue) {
        return null;
      }

      const value = String(rawValue);
      if (seen.has(value)) {
        return null;
      }

      const rawLabel =
        data.merchant_name ??
        data.name ??
        data.business_name ??
        data.trade_name ??
        data.company_name ??
        rawValue;

      seen.add(value);
      return {
        value,
        label: String(rawLabel),
      };
    })
    .filter((option): option is MerchantOption => option !== null);
};

const parseSettlementsPayload = (payload: any) => {
  const data = extractArrayFromPayload(payload);
  const countCandidates = [
    payload?.count,
    payload?.results?.count,
    payload?.results?.total,
    payload?.data?.count,
    payload?.data?.total,
    payload?.total,
  ];
  const count =
    countCandidates
      .map(candidate => (typeof candidate === 'number' ? candidate : Number(candidate)))
      .find(candidate => !Number.isNaN(candidate)) ?? data.length;

  return {
    data,
    count,
  };
};

export const SettlementPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [merchantFilter, setMerchantFilter] = useState<string>('');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any | null>(null);
  const [Settlement, setSettlement] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [SettlementearchTerm, setSettlementearchTerm] = useState('');
  const [merchantSettlementearchTerm, setMerchantSettlementearchTerm] = useState('');
  const [Settlementtats, setSettlementtats] = useState('');
  const [merchants, setMerchants] = useState<MerchantOption[]>([]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50); // Default rows per page

  // Sorting state
  const [sortField, setSortField] = useState<keyof any>('transaction_date_time');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const [searchKey, setSearchKey] = useState<string>('settlement_id');
  const [searchValue, setSearchValue] = useState<string>('');
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [filters, setFilters] = useState<SettlementFilters>({
    searchTerm: '',
    statusFilter: '',
    dateRange: { startDate: '', endDate: '' },
  });

  const STATUSES = {
    UNSETTLED: 'UNSETTLED',
    HOLD: 'HOLD',
    SETTLED: 'SETTLED',
  } as const;

  const { user } = useSuperadminAuth();

  
  if (!user) return <div>No Access</div>;

  const hasPermission = (permission: string) => {
    if (user.is_superuser) return true; // superuser has everything
    return user.permission_list?.includes(permission);
  };

  console.log('user', user?.permission_list);


  // Load Settlement with server-side pagination
  useEffect(() => {
    loadSettlement();
    loadSettlementtats();
  }, [page, rowsPerPage, sortField, sortDirection]);

  // Load merchants for dropdown on first render
  useEffect(() => {
    const loadMerchants = async () => {
      try {
        const resp = await settlementListService.getMerchants(1, 1000);
        const list = normalizeMerchantOptions(resp);
        setMerchants(list);
      } catch (e) {
        console.error('Error loading merchants:', e);
      }
    };
    loadMerchants();
  }, []);

  const loadSettlement = async () => {
    setIsLoading(true);
    try {
      const currentPage = page + 1; // Convert 0-based to 1-based page
      const apiResponse = await settlementListService.getSettlements(currentPage, rowsPerPage);

      const parsed = parseSettlementsPayload(apiResponse);
      setSettlement(parsed.data);
      setTotalCount(parsed.count);
    } catch (error) {
      console.error('Error loading Settlement:', error);
      setSettlement([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSettlementtats = async () => {
    try {
      const stats = await settlementListService.getSettlementsStats();
      setSettlementtats(stats);
    } catch (error) {
      console.error('Error loading transaction stats:', error);
      // Keep default stats on error
    }
  };

  // Client-side search filtering (only for search term)
  // const filteredSettlement = Settlement.filter(txn => {
  //   if (!searchTerm) return true;
  //   return (
  //     txn.transaction_id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
  //     txn.merchant_transaction_id?.toString().toLowerCase().includes(searchTerm.toLowerCase())
  //   );
  // });
  const filteredSettlement = Settlement;

  // Breadcrumbs
  const breadcrumbConfig = getBreadcrumbConfig('/Settlement');
  const breadcrumbs = useCommonBreadcrumbs({
    hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs,
    customBreadcrumbs: breadcrumbConfig.hideBreadcrumbs ? [] : undefined,
  });

  const finalBreadcrumbs =
    breadcrumbs.length > 0
      ? breadcrumbs
      : [
          {
            label: 'Dashboard',
            href: '/merchant/dashboard',
            onClick: () => navigate('/merchant/dashboard'),
          },
          {
            label: 'Settlements',
            active: true,
          },
        ];

  // Formatters
  const formatSettlementCurrency = (amount: number): string =>
    formatCurrency(amount, 'INR', 'en-IN');

  const formatSettlementDate = (dateString: string): string =>
    formatMomentDate(dateString, 'MMM DD, YYYY HH:mm');

  const formatAmountDisplay = (value: unknown): string => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    const numeric =
      typeof value === 'number' ? value : Number.isNaN(Number(value)) ? null : Number(value);

    if (numeric === null) {
      return String(value);
    }

    return formatSettlementCurrency(numeric);
  };

  // Subtitle generator
  const getSubtitle = () => {
    const parts: string[] = [];

    if (statusFilter) {
      parts.push(`Status: ${STATUSES[statusFilter as keyof typeof STATUSES] || statusFilter}`);
    }

    if (merchantFilter) {
      const merchantLabel = merchants.find(option => option.value === merchantFilter)?.label;
      parts.push(`Merchant: ${merchantLabel || merchantFilter}`);
    }

    if (searchTerm) {
      parts.push(`Search: "${searchTerm}"`);
    }

    const total = searchTerm ? filteredSettlement.length : totalCount;
    const base = 'Manage payment Settlements';

    return parts.length > 0
      ? `${base} • ${parts.join(' • ')} (${total} ${total === 1 ? 'transaction' : 'Settlements'})`
      : `${base} (${total} ${total === 1 ? 'transaction' : 'Settlements'})`;
  };

  // Pagination handlers - these will trigger API calls via useEffect
  const handleChangePage = (_event: unknown, newPage: number) => {
    setIsLoading(true);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing page size
  };

  // Sorting handlers - these will trigger API calls via useEffect
  const handleSort = (field: keyof any) => {
    setIsLoading(true);
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(0); // Reset to first page when sorting
  };

  const handleFilterPanelOpen = () => {
    setIsFilterPanelOpen(true);
  };

  const handleFilterPanelClose = () => {
    setIsFilterPanelOpen(false);
  };

  // Get sort icon for column
  const getSortIcon = (field: keyof any) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ArrowUpwardIcon fontSize="small" />
    ) : (
      <ArrowDownwardIcon fontSize="small" />
    );
  };

  // Table columns configuration
  const columns = [
    { field: 'settlement_id', headerName: 'Settlement ID', sortable: true, align: 'left' as const },
    // { field: 'settlement_uuid', headerName: 'Settlement UID', sortable: true, align: 'left' as const },
    {
      field: 'merchant_name',
      headerName: 'Merchant Name',
      sortable: true,
      align: 'right' as const,
    },
    { field: 'settlement_amount', headerName: 'Amount', sortable: true, align: 'right' as const },
    // { field: 'payment_gateway', headerName: 'Payment Gateway', sortable: true, align: 'left' as const },
    { field: 'settlement_status', headerName: 'Status', sortable: true, align: 'center' as const },
    { field: 'payout_date_time', headerName: 'Created At', sortable: true, align: 'left' as const },
  ];

  // Drawer handlers
  const handleOpenFilterDrawer = (transaction: any) => {
    setSelectedTransaction(transaction);
    setIsFilterDrawerOpen(true);
  };

  const handleCloseFilterDrawer = () => {
    setIsFilterDrawerOpen(false);
    setSelectedTransaction(null);
  };

  // const handleDownloadInovice = async (settlementId: number) => {
  //   console.log('Downloading invoice for settlement ID:', settlementId);

  //   const url = `/merchant/v1/download-settlement/`;
  //   const response = await api.post(url);
  //   console.log('Dashboard chart data response:', response);
  // };

  const handleDownloadInovice = async (settlementId: string) => {
    const url = `/backoffice/v1/download-settlement-file`;
    try {
      // Ask Axios to treat the response as binary data (Excel file)
      const response = await api.post(
        url,
        { settlement_id: settlementId },
        { responseType: 'blob' } // 👈 important for binary file downloads
      );

      // Create a Blob from the binary Excel data
      const blob = new Blob([response.data as BlobPart], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // official Excel MIME type
      });

      // Create a temporary URL for the blob
      const downloadUrl = window.URL.createObjectURL(blob);

      // Create an invisible <a> element to trigger the file download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `invoice-${settlementId}.xlsx`; // 👈 filename for Excel
      document.body.appendChild(link);
      link.click();

      // Clean up temporary elements
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      console.log('Excel invoice downloaded successfully!');
    } catch (error) {
      console.error('Error downloading Excel invoice:', error);
    }
  };

  const onStatusFilterChangeData = async (status?: string | null) => {
    const statusData = String(status ?? '').trim();

    if (statusData) {
      try {
        setIsLoading(true);
        const response = await settlementListService.getSettlementStatusData(statusData);
        const parsed = parseSettlementsPayload(response);

        setSettlement(parsed.data);
        setTotalCount(parsed.count);
      } catch (error) {
        console.error(error);
        setSettlement([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    } else {
      setPage(0);
      loadSettlement();
    }
  };

  const handleRefresh = () => {
    loadSettlement();
    loadSettlementtats();
  };

  //   const onDateRangeFilterChange = async (startDate?: Date | null, endDate?: Date | null) => {
  //   // Format dates to YYYY-MM-DD
  //   const start = startDate ? startDate.toISOString().split('T')[0] : '';
  //   const end = endDate ? endDate.toISOString().split('T')[0] : '';

  //   // If both dates are empty, reset
  //   if (!start && !end) {
  //     setPage(0);
  //     loadSettlement();
  //     return;
  //   }

  //   try {
  //     setIsLoading(true);

  //     // Replace this with your API call that accepts date range
  //     const response = await settlementListService.getSettlementByDateRange(start, end);
  //     const parsed = parseSettlementsPayload(response);

  //     setSettlement(parsed.data);
  //     setTotalCount(parsed.count);
  //   } catch (error) {
  //     console.error('Fetch error:', error);
  //     setSettlement([]);
  //     setTotalCount(0);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const onClearFilters = () => {
    setIsLoading(true);
    setSearchTerm('');
    setStatusFilter('');
    setMerchantFilter('');
    setSettlementearchTerm('');
    setMerchantSettlementearchTerm('');
    setPage(0);
    loadSettlement();
  };

  const onSearchTermChange = async () => {
    const value = searchValue.trim();

    if (!value) {
      setSettlement([]);
      setSearchTerm('');
      return;
    }

    try {
      const response = await settlementListService.getSettlementData(
        searchKey === 'settlement_id' ? value : undefined,
        searchKey === 'merchant_transaction_id' ? value : undefined
      );

      const parsed = parseSettlementsPayload(response);
      setSettlement(parsed.data);
      setTotalCount(parsed.count);
      setSearchTerm(value);
    } catch (error) {
      setSettlement([]);
      console.error('Fetch error:', error);
    }
  };

  const handleSearch = () => {
    onSearchTermChange(searchKey, searchValue);
  };

  // Individual search term handlers
  // const handleSettlementearchChange = (value: string) => {
  //   setSettlementearchTerm(value);
  // };

  // const handleMerchantSettlementearchChange = (value: string) => {
  //   setMerchantSettlementearchTerm(value);
  // };

  // const onStatusFilterChangeData = (value: string) => {
  //   setSettlementearchStatusTerm(value);
  //   onStatusFilterChangeData();
  // };

  const onMerchantFilterChange = async (merchantUuid: string) => {
    const value = merchantUuid.trim();
    setMerchantFilter(value);
    setPage(0);
    if (value) {
      try {
        setIsLoading(true);
        const response = await settlementListService.getSettlementsByMerchant(value);
        const parsed = parseSettlementsPayload(response);
        setSettlement(parsed.data);
        setTotalCount(parsed.count);
      } catch (error) {
        setSettlement([]);
        setTotalCount(0);
        console.error('Fetch error:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      loadSettlement();
    }
  };

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleGenerateSettlement = async (formData: { merchant_uuid: string }) => {
    try {
      const response = await settlementService.generateSettlement({
        merchant_uuid: formData.merchant_uuid,
      });

      if (response.success) {
        toast.success(response.message || 'Settlement generated successfully');
        handleCloseDrawer();
      } else {
        toast.error(response.message || 'Failed to generate settlement');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const message = error.response?.data?.message || 'Failed to generate settlement';
        toast.error(message);
      } else {
        toast.error('Failed to generate settlement');
      }
      throw error; // Re-throw to let the drawer handle submitting state
    }
  };

  // Header actions
  const headerActions: PageHeaderAction[] = [
    hasPermission('ADMIN_SETTLEMENT_GENERATE') && {
      label: 'Generate Settlement',
      icon: <AddIcon sx={{ fontSize: 16 }} />,
      onClick: handleOpenDrawer,
      variant: 'contained',
      size: 'medium',
    },
  ].filter(Boolean) as PageHeaderAction[];

  return (
    <Box>
      <PageHeader
        title="Settlements"
        subtitle={getSubtitle()}
        breadcrumbs={finalBreadcrumbs}
        actions={headerActions}
        showDivider={false}
      />

      <SettlementSummaryCards stats={Settlementtats} />

      <Box
        mb={3}
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        gap={2}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      >
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200, background: 'white' }}>
            <Select value={searchKey} onChange={e => setSearchKey(e.target.value)}>
              <MenuItem value="settlement_id">Settlement ID</MenuItem>
              <MenuItem value="merchant_name">Merchant Name</MenuItem>
            </Select>
          </FormControl>

          <TextField
            variant="outlined"
            type="search"
            placeholder={`Search by ${searchKey.replace(/_/g, ' ')}`}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              maxWidth: 300,
              '& .MuiOutlinedInput-root': {
                background: 'white',
              },
            }}
          />
          <Box>
            {/* Clear Filters */}
            <Button variant="text" size="small" onClick={onClearFilters}>
              Clear Filters
            </Button>
          </Box>
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box gap={1} mb={2}>
        {/* Filter Button */}
        <Button
          ref={filterButtonRef}
          variant="outlined"
          size="small"
          onClick={handleFilterPanelOpen}
          endIcon={<FilterIcon />}
          startIcon={<AddIcon />}
          sx={{
            minWidth: { xs: '100%', sm: 'auto' },
            textTransform: 'none',
          }}
        >
          Add Filters
        </Button>
        {isFilterPanelOpen && (
          <ClickAwayListener onClickAway={handleFilterPanelClose}>
            <Box>
              <FilterPanel
                open={isFilterPanelOpen}
                anchorEl={filterButtonRef.current}
                onClose={handleFilterPanelClose}
                filters={filters}
                merchants={merchants}
                merchantFilter={merchantFilter}
                onMerchantFilterChange={onMerchantFilterChange}
                onFiltersChange={onStatusFilterChangeData}
                onStatusFilterChangeData={onStatusFilterChangeData}
                onApplyFilters={onMerchantFilterChange}
                onClearFilters={onClearFilters}
              />
            </Box>
          </ClickAwayListener>
        )}
      </Box>
      {/* HTML Table with Pagination */}
      {hasPermission('ADMIN_SETTLEMENT_LIST_VIEW') ? (
        <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
          <TableContainer
            sx={{
              maxHeight: { xs: 400, sm: 600 },
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.1)',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.3)',
                borderRadius: 4,
              },
            }}
          >
            <Table stickyHeader sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
              <TableHead>
                <TableRow>
                  {columns.map(column => (
                    <TableCell
                      key={column.field}
                      align={column.align}
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#f5f5f5',
                        cursor: column.sortable ? 'pointer' : 'default',
                        '&:hover': column.sortable ? { backgroundColor: '#e0e0e0' } : {},
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '8px 4px', sm: '16px' },
                        whiteSpace: 'nowrap',
                      }}
                      onClick={() => column.sortable && handleSort(column.field as keyof any)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {column.headerName}
                        {column.sortable && getSortIcon(column.field as keyof any)}
                      </Box>
                    </TableCell>
                  ))}
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography variant="body1" color="textSecondary">
                          Loading Settlements...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredSettlement.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        No Settlements found matching your search criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSettlement.map(settlement => (
                    <TableRow key={settlement.settlement_id} hover>
                      <TableCell
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          padding: { xs: '8px 4px', sm: '16px' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            fontFamily="monospace"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            {String(settlement.settlement_id)}
                          </Typography>
                          <Tooltip title="Copy to clipboard">
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigator.clipboard.writeText(String(settlement.settlement_id))
                              }
                              className="copy-icon"
                              sx={{ ml: 0.5, p: 0.25, opacity: 100, transition: 'opacity 0.2s' }}
                            >
                              <ContentCopyIcon sx={{ fontSize: '15px' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      {/* <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FingerprintIcon fontSize="small" color="action" sx={{ mr: 1, opacity: 0.5, fontSize: { xs: '14px', sm: '16px' } }} />
                        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {String(settlement.settlement_uuid)}
                        </Typography>
                      </Box>
                    </TableCell> */}
                      <TableCell
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          padding: { xs: '8px 4px', sm: '16px' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            {String(settlement.merchant_name)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          padding: { xs: '8px 4px', sm: '16px' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            variant="body2"
                            fontWeight="center"
                            color="success"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            {formatSettlementCurrency(Number(settlement.settlement_amount))}
                          </Typography>
                        </Box>
                      </TableCell>
                      {/* <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BankIcon fontSize="small" color="primary" sx={{ mr: 1, opacity: 0.7, fontSize: { xs: '14px', sm: '16px' } }} />
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {settlement.payment_gateway && typeof settlement.payment_gateway === 'object' && 'payment_gateway_name' in settlement.payment_gateway
                            ? String((settlement.payment_gateway as any).payment_gateway_name)
                            : 'N/A'
                          }
                        </Typography>
                      </Box>
                    </TableCell> */}
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          padding: { xs: '8px 4px', sm: '16px' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Tooltip title={String(settlement.settlement_status)}>
                            <Chip
                              label={
                                STATUSES[settlement.settlement_status as keyof typeof STATUSES] ||
                                settlement.settlement_status
                              }
                              size="small"
                              //color={getStatusColor(settlement.settlement_status as Settlementtatus) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                              variant="outlined"
                              sx={{
                                fontWeight: 'normal',
                                fontSize: { xs: '0.625rem', sm: '0.75rem' },
                                borderRadius: '12px',
                                '& .MuiChip-label': { px: 1 },
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          padding: { xs: '8px 4px', sm: '16px' },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {formatSettlementDate(String(settlement.settlement_date))}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          padding: { xs: '8px 4px', sm: '16px' },
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenFilterDrawer(settlement)}
                            title="View Details"
                            sx={{ padding: { xs: '4px', sm: '8px' } }}
                          >
                            <ViewIcon
                              fontSize="small"
                              sx={{ fontSize: { xs: '16px', sm: '20px' } }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleDownloadInovice(settlement.settlement_uuid)}
                            title="downlaod Invoice"
                            sx={{ padding: { xs: '4px', sm: '8px' } }}
                          >
                            <DownloadIcon
                              fontSize="small"
                              sx={{ fontSize: { xs: '16px', sm: '20px' } }}
                            />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={searchTerm ? filteredSettlement.length : totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
          />
        </Paper>
      ) : (
        <Typography sx={{ p: 4 }} variant="body1" color="textSecondary">
          You do not have permission to view webhooks.
        </Typography>
      )}

      {/* Transaction Details Drawer */}
      <Drawer
        anchor="right"
        open={isFilterDrawerOpen}
        onClose={handleCloseFilterDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 920 },
            padding: { xs: 2, sm: 3 },
            pt: { xs: 8, sm: 10 },
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          },
        }}
      >
        {selectedTransaction ? (
          <Box display="flex" flexDirection="column" gap={2}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ fontWeight: 700 }}>
                      Settled Transactions
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Service Charge</TableCell>
                    <TableCell align="right">Service Tax</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(selectedTransaction.settled_transactions) &&
                  selectedTransaction.settled_transactions.length > 0 ? (
                    selectedTransaction.settled_transactions.map((item: any, index: number) => (
                      <TableRow key={item.transaction_id ?? index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {item.transaction_id ?? 'N/A'}
                        </TableCell>
                        <TableCell align="right">
                          {formatAmountDisplay(item.transaction_amount)}
                        </TableCell>
                        <TableCell align="right">
                          {formatAmountDisplay(item.service_charge)}
                        </TableCell>
                        <TableCell align="right">{formatAmountDisplay(item.service_tax)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No settled transactions available.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box>
              <Button variant="outlined" onClick={handleCloseFilterDrawer} fullWidth>
                Close
              </Button>
            </Box>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Select a settlement to view its details.
            </Typography>
            <Button variant="outlined" onClick={handleCloseFilterDrawer} fullWidth>
              Close
            </Button>
          </Box>
        )}
      </Drawer>

      <GenerateSettlementDrawer
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        onSubmit={handleGenerateSettlement}
      />
    </Box>
  );
};

export default SettlementPage;
